import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

// Define all possible user roles
type UserRole = 
  | 'admin' 
  | 'sales_assistant' 
  | 'invoices_clerk' 
  | 'store_manager' 
  | 'assistant_manager' 
  | 'stock_control_manager' 
  | 'warehouse_assistant' 
  | 'visionary';

// Define the structure for permissions
interface Permissions {
  [key: string]: boolean;
}

interface User {
  id: number;
  username: string;
  role: UserRole;
  permissions: Permissions;
  staff_details?: string;
  visionary_details?: string;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<{ user: User | null; role: UserRole }>;
  logout: () => Promise<void>;
  updateUserDetails: (details: any) => void;
  isStaff: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    // Check for existing session
    const checkSession = async () => {
      const sessionStr = localStorage.getItem('user_session');
      if (sessionStr) {
        try {
          // Parse the full user object including permissions
          const session: User = JSON.parse(sessionStr); 
          setUser(session);
        } catch (error) {
          console.error('Error parsing session:', error);
          localStorage.removeItem('user_session');
        }
      }
    };

    checkSession();
  }, []);

  // Helper function to check if the user is staff (non-visionary)
  const isStaff = () => {
    return user?.role !== undefined && user.role !== 'visionary';
  };

  const updateUserDetails = (details: any) => {
    if (!user) return;
    
    // Ensure permissions are preserved when updating details
    const updatedUser: User = { 
      ...user,
      visionary_details: JSON.stringify(details)
    };

    // Update local storage
    localStorage.setItem('user_session', JSON.stringify(updatedUser));
    // Update state
    setUser(updatedUser);
  };

  const login = async (username: string, password: string): Promise<{ user: User | null; role: UserRole }> => {
    const { data, error } = await supabase.rpc('handle_login', {
      p_username: username.toLowerCase(),
      p_password: password
    });

    if (error) throw error;
    if (!data || data.length === 0) throw new Error('Invalid credentials');

    const userData = data[0];
    
    // Construct the full user object including permissions
    const loggedInUser: User = { 
      id: userData.user_id,
      username: username,
      role: userData.user_role as UserRole,
      permissions: userData.permissions || {},
      staff_details: userData.staff_details ? JSON.stringify(userData.staff_details) : undefined,
      visionary_details: userData.visionary_details ? JSON.stringify(userData.visionary_details) : undefined
    };

    // Store session
    localStorage.setItem('user_session', JSON.stringify(loggedInUser));
    setUser(loggedInUser);

    // Return the full user object and role
    return { user: loggedInUser, role: loggedInUser.role }; 
  };

  const logout = async () => {
    // Clear all session-related data
    localStorage.removeItem('user_session');
    localStorage.removeItem('cartData'); // Also clear any cart data
    setUser(null);
    
    // Navigate to home page instead of login page
    window.location.href = '/'; 
  };

  // Ensure the context value provides the updated login function type
  return (
    <AuthContext.Provider value={{ user, login, logout, updateUserDetails, isStaff }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}